import React from 'react';
import {ActionCreator, createAction} from '@packages/utilities/create-action';
import {
  withSystem,
  withMeta,
  Plugins,
  ExpressionPath,
} from '@packages/systems/wfdl';
import {
  type ContextualExpression,
  ContextualExpressions,
  stripAnn,
} from '@packages/utilities/contextual-expression';
import {
  ExpressionEditor,
  updateExpressionAtAction,
  extendExpressionEditorActionAtPath,
  ExpressionEditorActionPayload,
} from '@packages/systems/ExpressionEditor-utils';
import {
  TEnum,
  TText,
  TRecord,
  TTypeConstructor,
  TListOfElement,
  EText,
  EEnum,
} from '@packages/systems/wfdl-ast';
import {qualify, typeName, pluginName} from '@packages/systems/wfdl-Qualified';
import {
  CART_STATES,
  PREVIEW_ITEMS_DEFAULT,
  ALIGN_DEFAULT,
  EASE_DEFAULT,
  OPEN_STATE_DEFAULT,
} from '@packages/systems/commerce/constants';
import {getProductCollection} from '@packages/systems/commerce/utils/CommerceCollectionUtils';
import {ShowIcon} from '@packages/systems/spring';
import {useCollections} from '@packages/systems/cms/client';
import {selectDatabaseId} from '@packages/systems/cms/client-selectors';
import {styledDiv, emotionStyled, tokens} from '@packages/utilities/styled';

import {COMMERCE_CART_OPEN_STATE_UPDATED} from '@packages/systems/designer/actions';
import {createIntegratedRootAlias} from '@packages/utilities/integrated-types';
import {selectMaybeLocalizedItemCountByCollectionIdByCurrentLocale} from '@packages/systems/localization';
import {
  useDesignerSelector,
  useDispatch,
  type DesignerStores,
} from '@packages/systems/designer/flux';
import {
  TEMP_PATH,
  TEMP_STATE_PATH,
} from '@packages/utilities/expression-path-constants';

const COMMERCE_PATH: ExpressionPath = [{in: 'Record', at: 'commerce'}];
const IS_OPEN_PATH: ExpressionPath = [
  ...TEMP_PATH,
  {in: 'Record', at: 'isOpen'},
];
const STATE_EMPTY_PATH: ExpressionPath = [
  ...TEMP_PATH,
  {in: 'Record', at: 'stateEmpty'},
];
const EASING_TYPE_PATH: ExpressionPath = [
  ...COMMERCE_PATH,
  {in: 'Record', at: 'easingType'},
];
const DURATION_PATH: ExpressionPath = [
  ...COMMERCE_PATH,
  {in: 'Record', at: 'duration'},
];
const OPEN_WHEN_PRODUCT_ADDED_PATH: ExpressionPath = [
  ...COMMERCE_PATH,
  {in: 'Record', at: 'openWhenProductAdded'},
];
const PREVIEW_ITEMS_PATH: ExpressionPath = [
  ...TEMP_PATH,
  {in: 'Record', at: 'previewItems'},
];
const CART_TYPE_PATH: ExpressionPath = [{in: 'Record', at: 'cartType'}];
const ALIGN_PATH: ExpressionPath = [{in: 'Record', at: 'align'}];
const OPEN_ON_PATH: ExpressionPath = [{in: 'Record', at: 'openOn'}];

const {getIn} = ContextualExpressions;

const Divider = styledDiv(({theme: {colors, gutters}}) => ({
  height: 1,
  backgroundColor: colors.backgroundDeep,
  width: `100%`,
  marginTop: gutters.base,
  marginBottom: gutters.base,
}));

const Grid = emotionStyled('div')({
  display: 'grid',
  gap: tokens.space[2],
});

const cartType = {
  children: TListOfElement,
  tag: withSystem(
    {
      label: 'Tag',
      private: true,
      defaultValue: 'div',
    },
    TTypeConstructor(qualify(pluginName('Basic'), typeName('TagsDropdown')))
  ),
  temp: TRecord({
    isOpen: withMeta(
      {
        icon: ShowIcon,
        secondaryLabel: 'Close cart',
      },
      withSystem(
        {
          label: 'Open cart',
          labelVariant: 'hidden',
          defaultValue: OPEN_STATE_DEFAULT,
        },
        TTypeConstructor(qualify(pluginName('Basic'), typeName('ToggleButton')))
      )
    ),
    state: withSystem(
      {
        defaultValue: CART_STATES.DEFAULT,
        label: 'State',
      },
      TTypeConstructor(qualify(pluginName('Basic'), typeName('CartStatesEnum')))
    ),
    stateEmpty: withSystem(
      {
        defaultValue: CART_STATES.EMPTY,
        label: 'State',
      },
      TTypeConstructor(
        qualify(pluginName('Basic'), typeName('CartStatesEmptyEnum'))
      )
    ),
    previewItems: withSystem(
      {
        label: 'Preview',
        defaultValue: PREVIEW_ITEMS_DEFAULT,
      },
      TTypeConstructor(qualify(pluginName('Basic'), typeName('Slider')))
    ),
  }),
  cartType: withSystem(
    {
      defaultValue: 'modal',
      label: 'Cart type',
    },
    TTypeConstructor(qualify(pluginName('Basic'), typeName('CartTypeEnum')))
  ),
  align: withSystem(
    {
      defaultValue: ALIGN_DEFAULT,
      label: 'Align',
    },
    TEnum({leftDropdown: 'Left', rightDropdown: 'Right'})
  ),
  openOn: withSystem(
    {
      defaultValue: 'CLICK',
      label: 'Open on',
    },
    TEnum({CLICK: 'Click', HOVER: 'Hover'})
  ),
  commerce: TRecord({
    type: withSystem(
      {
        defaultValue: 'cart-wrapper',
        private: true,
      },
      TText
    ),
    easingType: withSystem(
      {
        defaultValue: EASE_DEFAULT,
        label: 'Easing',
      },
      TTypeConstructor(qualify(Plugins.name('Basic'), typeName('EasingsEnum')))
    ),
    duration: withSystem(
      {label: 'Duration', defaultValue: 300},
      TTypeConstructor(
        qualify(pluginName('Basic'), typeName('SliderCartWrapper'))
      )
    ),
    openWhenProductAdded: withSystem(
      {
        labelVariant: 'hidden',
        label: 'Open when product is added',
        defaultValue: true,
      },
      TTypeConstructor(
        qualify(pluginName('Basic'), typeName('ReverseLabelBoolean'))
      )
    ),
  }),
} as const;

const isOpenToggled: ActionCreator<
  typeof COMMERCE_CART_OPEN_STATE_UPDATED,
  {selectedNodeNativeId: string | null}
> = createAction(COMMERCE_CART_OPEN_STATE_UPDATED);

const selectCollections = (state: DesignerStores) =>
  state.CollectionStore.collections;
const selectSelectedNodeNativeId = (state: DesignerStores) =>
  state.UiNodeStore.selectedNodeNativeId;

const Cart = ({
  expr,
  dispatch,
}: {
  expr: ContextualExpression;
  dispatch: (arg1?: any) => any;
}) => {
  const wfDispatch = useDispatch();
  const databaseId = useDesignerSelector(selectDatabaseId);
  /**
   * We need to load the collection metadata when this settings component opens since the collection
   * metadata has the total number of items on it, which powers the state logic below
   */
  useCollections(databaseId);
  const collections = useDesignerSelector(selectCollections);
  const itemCountByCollectionId = useDesignerSelector(
    selectMaybeLocalizedItemCountByCollectionIdByCurrentLocale
  );
  const selectedNodeNativeId = useDesignerSelector(selectSelectedNodeNativeId);

  const onChangeAt =
    (at: ExpressionPath) => (action: ExpressionEditorActionPayload) => {
      if (at === IS_OPEN_PATH) {
        wfDispatch(isOpenToggled({selectedNodeNativeId}));
      }
      return extendExpressionEditorActionAtPath(dispatch)(at)(action);
    };

  const expressionComponent = (path: ExpressionPath) => {
    const exprAt = getIn(expr, path);
    if (!exprAt) {
      throw Error('No expression found at this path');
    } else {
      return (
        <ExpressionEditor expression={exprAt} dispatch={onChangeAt(path)} />
      );
    }
  };

  const IsOpen = expressionComponent(IS_OPEN_PATH);
  const CartTypes = expressionComponent(CART_TYPE_PATH);
  const Duration = expressionComponent(DURATION_PATH);
  const PreviewItems = expressionComponent(PREVIEW_ITEMS_PATH);
  const Align = expressionComponent(ALIGN_PATH);
  const OpenOn = expressionComponent(OPEN_ON_PATH);
  const EasingType = expressionComponent(EASING_TYPE_PATH);
  const OpenWhenProductAdded = expressionComponent(
    OPEN_WHEN_PRODUCT_ADDED_PATH
  );
  const State = expressionComponent(TEMP_STATE_PATH);
  const StateEmpty = expressionComponent(STATE_EMPTY_PATH);

  const productCollection = getProductCollection(collections);
  const itemsCount = productCollection
    ? itemCountByCollectionId.get(productCollection._id) || 0
    : 0;
  const cartTypeExpr = getIn(expr, CART_TYPE_PATH);
  const cartTypeValue = cartTypeExpr
    ? ContextualExpressions.getText(stripAnn(cartTypeExpr)) || ''
    : '';
  const stateExpr = getIn(expr, TEMP_STATE_PATH);
  const stateValue =
    stateExpr && ContextualExpressions.getText(stripAnn(stateExpr));

  React.useEffect(() => {
    if (cartTypeValue.includes('Dropdown')) {
      dispatch(updateExpressionAtAction(EText('dropdown'), CART_TYPE_PATH));
      dispatch(updateExpressionAtAction(EEnum(cartTypeValue), ALIGN_PATH));
    }
  }, [cartTypeValue, dispatch]);

  return (
    <Grid>
      {IsOpen}
      <Divider />
      {CartTypes}
      {cartTypeValue.includes('dropdown') ? (
        <>
          {Align}
          {OpenOn}
        </>
      ) : null}
      {EasingType}
      {Duration}
      {OpenWhenProductAdded}
      <Divider />
      {itemsCount > 0 ? State : StateEmpty}
      {stateValue !== CART_STATES.EMPTY && itemsCount ? PreviewItems : null}
    </Grid>
  );
};

const CartDataType = createIntegratedRootAlias({
  params: ['extraAtomFields'],
  fields: cartType,
  render: Cart,
});

export default CartDataType;
